import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteGstApi,
  getGstApi,
  getGstsApi,
  postGstApi,
  updateGstApi,
} from "../../api/ecommerce/GstApi";

const initialState = {
  gst: null,
  gsts: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

const getErrorPayload = (error) => ({
  errorMessage:
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong",
});

const createGstThunk = (type, api) =>
  createAsyncThunk(type, async (payload, { rejectWithValue }) => {
    try {
      const response = await api(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue(getErrorPayload(error));
    }
  });

export const getGst = createGstThunk("gst/getGst", getGstApi);

export const getGsts = createGstThunk("gst/getGsts", getGstsApi);

export const postGst = createGstThunk("gst/postGst", postGstApi);

export const updateGst = createGstThunk("gst/updateGst", updateGstApi);

export const deleteGst = createGstThunk("gst/deleteGst", deleteGstApi);

const getResponse = (payload) => payload?.response ?? payload;
const getError = (action) => action?.payload?.errorMessage || null;

const gstSlice = createSlice({
  name: "gst",
  initialState,
  reducers: {
    clearGstError: (state) => {
      state.error = null;
    },
    clearGstState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getGst.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGst.fulfilled, (state, action) => {
        state.gst = getResponse(action.payload);
        state.loading = false;
      })
      .addCase(getGst.rejected, (state, action) => {
        state.loading = false;
        state.error = getError(action);
      })
      .addCase(getGsts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGsts.fulfilled, (state, action) => {
        state.gsts = getResponse(action.payload) || [];
        state.loading = false;
      })
      .addCase(getGsts.rejected, (state, action) => {
        state.loading = false;
        state.error = getError(action);
      })
      .addCase(postGst.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(postGst.fulfilled, (state, action) => {
        state.gst = getResponse(action.payload);
        state.saving = false;
      })
      .addCase(postGst.rejected, (state, action) => {
        state.saving = false;
        state.error = getError(action);
      })
      .addCase(updateGst.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateGst.fulfilled, (state, action) => {
        state.gst = getResponse(action.payload);
        state.saving = false;
      })
      .addCase(updateGst.rejected, (state, action) => {
        state.saving = false;
        state.error = getError(action);
      })
      .addCase(deleteGst.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteGst.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteGst.rejected, (state, action) => {
        state.deleting = false;
        state.error = getError(action);
      });
  },
});

export const { clearGstError, clearGstState } = gstSlice.actions;

export default gstSlice.reducer;
